// Pure logic for building a localized Google SERP URL.
// Extracted from SerpChecker.js so the geo-signal selection (the P2 bug) is
// isolated and unit-testable. UULE v1/v2 encoding is moved verbatim — only the
// *selection* of which UULE to use changed (country now drives it).

import { COUNTRY_COORDS } from '../data/countryCoords';

// Helper: Uint8Array -> Base64 string
const uint8ArrayToBase64 = (bytes) => {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const varint = (n) => {
  const out = [];
  while (true) {
    const b = n & 0x7f;
    n >>>= 7;
    out.push(n ? b | 0x80 : b);
    if (!n) break;
  }
  return new Uint8Array(out);
};

const concatUint8Arrays = (arrays) => {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
};

// UULE v1 (protobuf canonical-name format). Google ignored it in our tests, even
// with canonical names (LRN-029); only kept as the fallback for a country with no
// entry in COUNTRY_COORDS.
export const uuleV1FromCanonical = (canonicalName, role = 2, producer = 32) => {
  if (!canonicalName) return '';
  const nameBuf = new TextEncoder().encode(canonicalName);
  const parts = [
    new Uint8Array([0x08]), varint(role),
    new Uint8Array([0x10]), varint(producer),
    new Uint8Array([0x22]), varint(nameBuf.length), nameBuf,
  ];
  const protobufData = concatUint8Arrays(parts);
  let b64 = uint8ArrayToBase64(protobufData);
  b64 = b64.replace(/\+/g, '-').replace(/\//g, '_');
  return 'w+' + b64;
};

// UULE v2 (lat/lng format). Used for city searches (geocoded coords) and for
// country searches (the country's representative point, COUNTRY_COORDS).
export const uuleV2FromLatLng = (lat, lng, opts = {}) => {
  const radius = opts.radius || 150 * 620; // 93000 m (valentin.app default)
  const role = opts.role || 1;
  const producer = opts.producer || 12;
  const provenance = opts.provenance || 6;
  const timestamp = opts.timestamp || Date.now() * 1000;

  const lat_e7 = Math.round(lat * 1e7);
  const lng_e7 = Math.round(lng * 1e7);

  const txt = `role:${role}\nproducer:${producer}\nprovenance:${provenance}\ntimestamp:${timestamp}\nlatlng{\nlatitude_e7:${lat_e7}\nlongitude_e7:${lng_e7}\n}\nradius:${radius}`;

  let b64 = btoa(txt);
  b64 = b64.replace(/\+/g, '-').replace(/\//g, '_');
  // Space prefix -> URLSearchParams serializes it back to '+'
  return `a ${b64}`;
};

/**
 * Build the localized Google search URL.
 * @param {object} p
 * @param {string} p.keyword        search query
 * @param {string} p.countryCode    ISO country code (e.g. 'ES') -> gl
 * @param {string} p.countryLabel   country display name (e.g. 'Spain') -> UULE v1 fallback
 * @param {string} [p.countryCanonical]  Google canonical name when it differs from the label -> UULE v1 fallback
 * @param {{lat:string|number,lng:string|number}|null} p.coords  precise city coords -> UULE v2 + ll
 *   (without them, the country's COUNTRY_COORDS point -> UULE v2, no ll)
 * @param {string} p.language       hl
 * @param {number|string} p.num     results per page
 * @param {boolean} p.safe          SafeSearch
 * @param {number} [p.uuleTimestamp] fixed V2 timestamp (µs) for deterministic example URLs
 * @returns {string} https://www.google.com/search?...
 */
export function buildSerpUrl({
  keyword, countryCode, countryLabel, countryCanonical, coords, language, num, safe, uuleTimestamp,
}) {
  const v2opts = uuleTimestamp ? { timestamp: uuleTimestamp } : {};
  // Country-only: the same V2 format as a city, pointed at the capital, with the
  // default 93 km radius (the value the city path already uses and the one that
  // localized Mbabane in the LRN-029 test). No ll: that would narrow the search
  // to the capital's map viewport; gl keeps the country signal.
  const countryPoint = COUNTRY_COORDS[String(countryCode).toUpperCase()];
  let uule;
  if (coords) uule = uuleV2FromLatLng(parseFloat(coords.lat), parseFloat(coords.lng), v2opts);
  else if (countryPoint) uule = uuleV2FromLatLng(countryPoint[0], countryPoint[1], v2opts);
  else uule = uuleV1FromCanonical(countryCanonical || countryLabel || countryCode);

  const params = new URLSearchParams({
    q: keyword,
    hl: language,
    gl: String(countryCode).toLowerCase(),
    uule,
    num: String(num),
    safe: safe ? 'active' : 'off',
    pws: '0',
  });
  if (coords) params.append('ll', `${coords.lat},${coords.lng}`);

  return `https://www.google.com/search?${params.toString()}`;
}
